import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

interface SwitchUserDialogProps {
    /** Who is signed in now. */
    currentName: string;
    /** Who the tapped card belongs to. */
    incomingName: string;
    /** Local changes the current user has not managed to sync yet. */
    pendingCount: number;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Asked before a tapped login card replaces the signed-in user. A card tap is
 * deliberate, but it is also easy to do by accident with a card in a pocket,
 * and the person losing the session may have unsynced work.
 *
 * Works offline: both names are decoded from the tokens on device.
 */
export default function SwitchUserDialog({
    currentName,
    incomingName,
    pendingCount,
    onConfirm,
    onCancel
}: SwitchUserDialogProps) {
    return (
        <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>
                <FormattedMessage id="auth.switch.title" />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1.5}>
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage
                            id="auth.switch.body"
                            values={{
                                current: <strong>{currentName}</strong>,
                                incoming: <strong>{incomingName}</strong>
                            }}
                        />
                    </Typography>
                    {pendingCount > 0 && (
                        <Alert severity="info">
                            <FormattedMessage
                                id="auth.switch.pending"
                                values={{
                                    count: pendingCount,
                                    current: currentName
                                }}
                            />
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onCancel}>
                    <FormattedMessage id="common.cancel" />
                </Button>
                <Button variant="contained" onClick={onConfirm}>
                    <FormattedMessage id="auth.switch.confirm" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
